/**
 * «Объявления» во вкладке «Лиды Авито»: по объявлению, городу или заголовку —
 * показы → просмотры → контакты (статистика Авито, копия `avito_item_day`) →
 * обращения → записали → бронь (правила «Лидов Авито») и цена каждого шага.
 * Куда переносить деньги продвижения. Спека
 * `docs/specs/2026-10-01-avito-stats-design.md`; числа — `prokat-runner avito-items`.
 */
import { useQuery } from "@tanstack/react-query";

import {
  type ItemsGroup,
  type ItemsRow,
  loadAvitoItems,
} from "@/lib/avitoItems";

const SERVICE: Record<string, string> = {
  cpa_click_package: "оплата за просмотры",
  tariff_ext: "тариф",
};

const rub = (kop: number) =>
  `${Math.round(kop / 100).toLocaleString("ru-RU")} ₽`;
const price = (x: number | null) =>
  x === null ? "—" : `${Math.round(x).toLocaleString("ru-RU")} ₽`;
const pct = (x: number | null) =>
  x === null ? "—" : `${(x * 100).toFixed(1)}%`;

type SortKey =
  | "spend"
  | "impressions"
  | "views"
  | "ctr"
  | "contacts"
  | "conv"
  | "cost_contact"
  | "chats"
  | "booked"
  | "bron"
  | "cost_booked";

const COLS: { key: SortKey; label: string; hint: string }[] = [
  { key: "impressions", label: "Показы", hint: "появления в выдаче" },
  { key: "views", label: "Просмотры", hint: "открыли карточку" },
  { key: "ctr", label: "CTR", hint: "просмотры / показы — фото, заголовок, цена" },
  { key: "contacts", label: "Контакты", hint: "написали в чат или открыли телефон (Авито)" },
  { key: "conv", label: "Конв.", hint: "контакты / просмотры — описание, цена, доверие" },
  { key: "spend", label: "Расход", hint: "все списания Авито по объявлению" },
  { key: "cost_contact", label: "₽/контакт", hint: "расход / контакты" },
  { key: "chats", label: "Обращений", hint: "чаты открытой линии Bitrix (один чат за день — одно)" },
  { key: "booked", label: "Записали", hint: "сделка обращения дошла до «ждем клиента»" },
  { key: "bron", label: "Бронь", hint: "сделка обращения дошла до «Забронировано»" },
  { key: "cost_booked", label: "₽/запись", hint: "расход / записали" },
];

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
      data-annot={`объявления авито «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[22px] font-semibold leading-none tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** Дороже среднего вдвое — красным, вдвое дешевле — зелёным. */
function tone(x: number | null, avg: number | null): string {
  if (x === null || avg === null) return "";
  if (x >= avg * 2) return "text-err";
  if (x <= avg / 2) return "text-ok";
  return "";
}

function value(g: ItemsRow, k: SortKey): number {
  const v = g[k];
  // Пустая цена — в конец при сортировке «дешевле сверху».
  return v === null ? Number.POSITIVE_INFINITY : v;
}

export function AvitoItems({
  from,
  to,
  group,
  sort,
  set,
}: {
  from: string;
  to: string;
  group: ItemsGroup;
  sort: string;
  set: (patch: Record<string, string>) => void;
}) {
  const q = useQuery({
    queryKey: ["avito-items", from, to, group],
    queryFn: () => loadAvitoItems(from, to, group),
  });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const groups = (
    <div className="flex gap-1" role="group" aria-label="Разрез">
      {(
        [
          ["item", "по объявлениям"],
          ["city", "по городам"],
          ["title", "по заголовкам"],
        ] as const
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => set({ by: k === "item" ? "" : k })}
          aria-pressed={group === k}
          className={seg(group === k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
  if (q.isPending)
    return (
      <>
        {groups}
        <div className="text-[12.5px] text-fg-muted">
          Считаю объявления Авито…
        </div>
      </>
    );
  if (q.isError)
    return (
      <>
        {groups}
        <div className="text-[12.5px] text-err">
          Не удалось получить объявления: {String(q.error.message)}
        </div>
      </>
    );
  const r = q.data;
  const t = r.total;
  // Сортировка: `-spend` — по убыванию; цены по умолчанию — дешевле сверху.
  const desc = !sort.startsWith("+");
  const key = (sort.replace(/^[+-]/, "") || "spend") as SortKey;
  const rows = [...r.rows].sort((a, b) => {
    const d = value(a, key) - value(b, key);
    return desc ? -d || b.spend - a.spend : d || b.spend - a.spend;
  });
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const td = "whitespace-nowrap px-3 py-1 tabular-nums text-right";
  const head = r.groups[r.group];
  return (
    <>
      {groups}
      {r.days_loaded < r.days_total && (
        <div className="rounded-[var(--radius-ui)] border border-warn bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
          Статистика Авито скачана за {r.days_loaded} из {r.days_total} дней
          периода — история докачивается фоном (≈ день в минуту, лимит Авито).
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label="Показы → просмотры"
          value={t.views.toLocaleString("ru-RU")}
          hint={`${t.impressions.toLocaleString("ru-RU")} показов · CTR ${pct(t.ctr)}`}
        />
        <Stat
          label="Контакты"
          value={t.contacts}
          hint={`конв. ${pct(t.conv)} · чат ${t.contacts_messenger} · телефон ${t.contacts_phone}`}
        />
        <Stat
          label="Расход по объявлениям"
          value={rub(t.spend)}
          hint={r.spending
            .map((x) => `${SERVICE[x.service] ?? x.service} ${rub(x.amount)}`)
            .join(" · ")}
        />
        <Stat
          label="Цена контакта"
          value={price(t.cost_contact)}
          hint="расход / контакты Авито"
        />
        <Stat
          label="Обращения → запись"
          value={`${t.booked} из ${t.chats}`}
          hint={`хотят платье ${t.dress} · бронь ${t.bron} · договор ${t.contract}`}
        />
        <Stat
          label="Цена записи / брони"
          value={price(t.cost_booked)}
          hint={`бронь ${price(t.cost_bron)}`}
        />
      </div>
      <section
        className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
        data-annot={`объявления авито ${head}`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className={`${th} pl-4`}>
                  {head.charAt(0).toUpperCase() + head.slice(1)} · {r.rows.length}
                </th>
                {COLS.map((c) => (
                  <th key={c.key} className={`${th} text-right`} title={c.hint}>
                    <button
                      type="button"
                      onClick={() =>
                        set({
                          sort:
                            key === c.key
                              ? `${desc ? "+" : "-"}${c.key}`
                              : `${c.key.startsWith("cost") ? "+" : "-"}${c.key}`,
                        })
                      }
                      className={key === c.key ? "text-fg" : "hover:text-fg"}
                    >
                      {c.label}
                      {key === c.key ? (desc ? " ↓" : " ↑") : ""}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.key} className="border-t border-border">
                  <td className="max-w-[360px] py-1 pl-4 pr-3">
                    <div className="truncate" title={g.label}>
                      {g.url ? (
                        <a
                          href={g.url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          {g.label}
                        </a>
                      ) : (
                        g.label
                      )}
                    </div>
                    {r.group === "item" && g.item_id ? (
                      <div className="truncate text-[11.5px] text-fg-faint">
                        {g.city}
                        {g.price ? ` · ${g.price.toLocaleString("ru-RU")} ₽` : ""}
                        {g.status && g.status !== "active" ? ` · ${g.status}` : ""}
                        {` · №${g.item_id}`}
                      </div>
                    ) : r.group !== "item" && g.items ? (
                      <div className="text-[11.5px] text-fg-faint">
                        объявлений {g.items}
                      </div>
                    ) : null}
                  </td>
                  <td className={td}>{g.impressions.toLocaleString("ru-RU")}</td>
                  <td className={td}>{g.views.toLocaleString("ru-RU")}</td>
                  <td className={td}>{pct(g.ctr)}</td>
                  <td className={td}>{g.contacts || ""}</td>
                  <td
                    className={`${td} ${g.views >= 100 ? tone(t.conv, g.conv) : ""}`}
                  >
                    {pct(g.conv)}
                  </td>
                  <td className={td}>{g.spend ? rub(g.spend) : ""}</td>
                  <td className={`${td} ${tone(g.cost_contact, t.cost_contact)}`}>
                    {price(g.cost_contact)}
                  </td>
                  <td className={td}>{g.chats || ""}</td>
                  <td className={td}>{g.booked || ""}</td>
                  <td className={td}>{g.bron || ""}</td>
                  <td className={`${td} ${tone(g.cost_booked, t.cost_booked)}`}>
                    {price(g.cost_booked)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className="text-[11.5px] text-fg-faint">
        Контакты — счёт Авито (написал в чат или открыл телефон), обращения —
        чаты Bitrix за день; объявление обращения — из ссылки Bitrix или по
        чату Авито. Тариф на объявления не делится. Цвет: вдвое дороже
        среднего — красный, вдвое дешевле — зелёный.
      </div>
    </>
  );
}
